import { useState } from 'react';
import { useNavigate } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { formatInteger } from '@brimo/shared';
import { clsx } from '../../../ds/clsx';
import { Banner, Button, Pill, Skeleton, TextLink, Toggle, useToast } from '../../../ds/components';
import { errorMessage } from '../../../lib/api';
import { queryRoots } from '../../../lib/events';
import { setDocumentVisibility, useDealDocumentRows, useDealDocuments } from '../api';
import type { DealDocumentRow, DealRecord } from '../types';
import { ParticipantsCard, SideCard } from './bits';
import styles from './record.module.css';

/**
 * Deal-Akte 07 „Dokumente": the missing banner with „Nur fehlende zeigen", the three party columns, the
 * request sidebar („Anfordern" → the contact's document request editor) and „Sichtbar für" with the
 * per-document customer toggles (`PATCH /documents/:id/visibility`).
 */
export function DocumentsTab({ deal }: { deal: DealRecord }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const documents = useDealDocuments(deal.id, true);
  const rows = useDealDocumentRows(deal.id, true);
  const [onlyMissing, setOnlyMissing] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);

  const visibilityOf = (id: string) => rows.data?.items.find((row) => row.id === id) ?? null;

  async function toggle(row: DealDocumentRow, next: boolean) {
    setBusy(row.id);
    try {
      await setDocumentVisibility(row.id, next);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: [...queryRoots.documents] }),
        queryClient.invalidateQueries({ queryKey: [...queryRoots.deal, deal.id, 'documents'] }),
      ]);
    } catch (error) {
      toast({ message: errorMessage(error) });
    } finally {
      setBusy(null);
    }
  }

  const data = documents.data;
  const missingCount = data?.missing.count ?? 0;

  return (
    <div className={styles.content}>
      <div className={styles.main}>
        {documents.isPending ? (
          <section className={styles.card}>
            <div className={styles.skeletonStack}>
              <Skeleton width="30%" height={14} />
              <Skeleton width="70%" />
              <Skeleton width="60%" />
            </div>
          </section>
        ) : null}
        {documents.isError ? <Banner tone="danger">{errorMessage(documents.error)}</Banner> : null}
        {data ? (
          <>
            <div className={clsx(styles.docBanner, missingCount === 0 && styles.docBannerOk)} role="status">
              <span className={styles.docBannerTitle}>{data.missing.title}</span>
              {data.missing.detail ? (
                <span className={styles.docBannerDetail}>{data.missing.detail}</span>
              ) : null}
              {missingCount > 0 ? (
                <span className={styles.docBannerLink}>
                  <TextLink as="button" size="small" onClick={() => setOnlyMissing((value) => !value)}>
                    {onlyMissing ? 'Alle zeigen' : 'Nur fehlende zeigen'}
                  </TextLink>
                </span>
              ) : null}
            </div>
            <div className={styles.docColumns}>
              {data.groups.map((group) => {
                const items = onlyMissing ? group.items.filter((item) => !item.done) : group.items;
                return (
                  <section
                    key={group.party}
                    className={clsx(styles.card, styles.docColumn)}
                    aria-label={group.partyLabel}
                  >
                    <div className={styles.docColumnHead}>
                      <span className={styles.docColumnTitle}>{group.partyLabel}</span>
                      <span className={styles.docColumnCount}>
                        {formatInteger(group.done)} von {formatInteger(group.total)}
                      </span>
                    </div>
                    <div className={styles.docRows}>
                      {items.length === 0 ? (
                        <div className={styles.docEmpty}>
                          {onlyMissing ? 'Nichts fehlt.' : 'Keine Unterlagen.'}
                        </div>
                      ) : null}
                      {items.map((item) => {
                        const visibility = item.kind === 'document' ? visibilityOf(item.id) : null;
                        return (
                          <div key={item.id} className={styles.docRow}>
                            <span
                              className={clsx(styles.docMark, item.done && styles.docMarkDone)}
                              aria-hidden="true"
                            >
                              {item.done ? '✓' : ''}
                            </span>
                            <span className={styles.docText}>
                              {item.canOpen && item.fileId ? (
                                <a
                                  className={styles.docNameLink}
                                  href={`/api/files/${item.fileId}`}
                                  target="_blank"
                                  rel="noopener"
                                >
                                  {item.name}
                                </a>
                              ) : (
                                <span className={styles.docName}>{item.name}</span>
                              )}
                              <span className={styles.docMeta} style={{ display: 'block' }}>
                                {item.meta}
                              </span>
                            </span>
                            {visibility && !visibility.visibilityLocked ? (
                              <Toggle
                                label={`Für den Kunden sichtbar: ${item.name}`}
                                labelHidden
                                checked={visibility.visibleToCustomer}
                                disabled={busy === item.id}
                                onChange={(next) => void toggle(item, next)}
                              />
                            ) : (
                              <Pill variant={item.tone}>{item.statusLabel}</Pill>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </section>
                );
              })}
            </div>
          </>
        ) : null}
      </div>
      <div className={styles.side}>
        <SideCard label="Beim Kunden anfordern">
          <p className={styles.sideText}>
            {deal.contact.name} lädt fehlende Unterlagen über seinen Portal-Link hoch.
          </p>
          <div style={{ marginTop: 12 }}>
            <Button
              variant="secondary"
              size="small"
              onClick={() => void navigate(`${deal.contact.href}?editor=documents`)}
            >
              Anfordern
            </Button>
          </div>
          <p className={styles.footnote}>Mit Follow-up-Haken in 3 Tagen, falls nichts eingeht.</p>
        </SideCard>
        <SideCard label="Sichtbar für">
          <div className={styles.visibilityRows}>
            {(data?.visibilityLines ?? []).map((line) => (
              <div key={line.who} className={styles.visibilityRow}>
                <span className={styles.visibilityKey}>{line.who}</span>
                <span className={styles.visibilityValue}>{line.sees}</span>
              </div>
            ))}
          </div>
          <p className={styles.footnote}>Der Schalter an einer Unterlage gibt sie für den Kunden frei.</p>
        </SideCard>
        <ParticipantsCard deal={deal} />
      </div>
    </div>
  );
}
